import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Users } from 'lucide-react';
import predictionService, { PredictionGame } from '../../services/predictionService';
import api from '../../services/api';
import ThumbnailImage from '../ThumbnailImage';

const ACCENT = '#3fced1';

const buildFileUrl = (path?: string | null): string | null => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/**
 * Sidebar-Widget: das aktive Tippspiel als kleine Karte mit CTA.
 * Offene Spiele haben Vorrang vor laufenden; ohne aktives Spiel ausgeblendet.
 */
const SidebarPrediction: React.FC = () => {
  const [game, setGame] = useState<PredictionGame | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    predictionService
      .getAllGames()
      .then((list) => {
        if (cancelled) return;
        const games = (list || []).filter((g) => g && (g.slug || g._id));
        const active =
          games.find((g) => g.status === 'open') ||
          games.find((g) => g.status === 'running') ||
          null;
        setGame(active);
      })
      .catch(() => {
        /* Widget bleibt einfach ausgeblendet */
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // CLS-Fix (2026-09-01): Skeleton in Endhöhe (Bild + Titel + Meta + CTA)
  // statt null, solange die Tippspiele laden
  if (!loaded) {
    return (
      <section
        className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900"
        aria-hidden="true"
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="aspect-[4/3] w-20 shrink-0 animate-pulse rounded-lg border border-zinc-800 bg-zinc-800" />
            <div className="min-w-0 flex-1">
              <div className="h-4 w-16 animate-pulse rounded bg-zinc-800" />
              <div className="mt-2 h-6 w-4/5 animate-pulse rounded bg-zinc-800" />
            </div>
          </div>
          <div className="mt-2 h-4 w-3/5 animate-pulse rounded bg-zinc-800" />
          <div className="mt-3 h-9 w-full animate-pulse rounded-xl bg-zinc-800" />
        </div>
      </section>
    );
  }

  if (!game) return null;

  const href = `/tippspiele/${game.slug || game._id}`;
  // Titelbild des Tippspiels bevorzugen (2026-08-27), Fallback: erster
  // aktiver Hero-Slide (bisheriges Verhalten).
  const image =
    buildFileUrl(game.image) ??
    buildFileUrl((game.heroSlides || []).find((s: any) => s?.active !== false)?.image);
  const isOpen = game.status === 'open';

  return (
    <section className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
      <div className="p-4">
        <div className="flex items-start gap-3">
          {image && (
            <Link to={href} className="shrink-0">
              {/* Kleines 80px-Bild → 200er-Thumbnail statt Original */}
              <ThumbnailImage
                src={image}
                thumbSize={200}
                alt=""
                loading="lazy"
                className="aspect-[4/3] w-20 rounded-lg border border-zinc-800 object-cover"
              />
            </Link>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Tippspiel</div>
            <Link to={href}>
              <h2 className="mt-1 line-clamp-2 text-base font-bold text-white transition-colors hover:text-[#3fced1]">
                {game.name || 'Tippspiel'}
              </h2>
            </Link>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
          {game.tournament?.name && (
            <span className="inline-flex items-center gap-1">
              <Trophy size={12} style={{ color: ACCENT }} />
              <span className="truncate">{game.tournament.name}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Users size={12} style={{ color: ACCENT }} />
            {game.stats?.totalParticipants ?? 0} Tipper
          </span>
        </div>
        <Link
          to={href}
          className={`mt-3 block w-full rounded-xl py-2 text-center text-sm font-semibold transition-colors ${
            isOpen
              ? 'bg-[#3fced1] text-black hover:bg-[#35b5b8]'
              : 'border border-zinc-700 text-gray-300 hover:border-[#3fced1]/60 hover:text-white'
          }`}
        >
          {isOpen ? 'Jetzt tippen' : 'Zum Tippspiel'}
        </Link>
      </div>
    </section>
  );
};

export default SidebarPrediction;
